<template>
  <div :class="['i4pro-integration-logs', computedClass]">
    <div class="mb-4 flex justify-between items-center">
      <div class="flex flex-row gap-4">
        <v-select
          v-model="selectedAction"
          :options="actionOptions"
          label="label"
          value="value"
          placeholder="Selecionar Ação"
          :reduce="(item) => item.value"
          style="width: 320px"
        ></v-select>
        <v-select
          v-if="shouldShowPaidInstallments"
          v-model="selectedPaidInstallment"
          :options="paidInstallmentOptions"
          label="label"
          value="value"
          placeholder="Selecionar Parcela"
          :reduce="(item) => item.value"
          :loading="isLoadingPaidInstallments"
          style="width: 320px"
        ></v-select>
        <vs-button
          color="primary"
          type="border"
          :disabled="isProcessDisabled"
          @click="executeManualAction"
        >
          Processar
        </vs-button>
      </div>
      <vs-button
        icon="refresh"
        @click="reloadData"
        color="primary"
        type="border"
      />
    </div>
    <vs-collapse type="border">
      <vs-collapse-item v-for="(item, index) in logs" :key="item.id || index">
        <div class="flex flex-row items-center" slot="header">
          <span class="font-bold mr-4">{{ getActionLabel(item.action) }}</span>
          <vs-chip class="mr-3" :color="getStatusColor(item.status)">
            {{ getStatusLabel(item.status) }}
          </vs-chip>
          <vs-button
            v-if="canReprocess(item)"
            color="warning"
            type="border"
            size="small"
            :disabled="reprocessingSyncId === item.id"
            @click.stop="reprocessSync(item)"
          >
            {{
              reprocessingSyncId === item.id
                ? 'Reprocessando...'
                : 'Reprocessar'
            }}
          </vs-button>
        </div>
        <div class="flex flex-row flex-wrap gap-4 mb-3">
          <div
            v-if="item.tries !== null && item.tries !== undefined"
            class="flex flex-col w-1/6"
          >
            <span class="font-bold">Tentativas:</span>
            <span>{{ item.tries }}</span>
          </div>
          <div v-if="item.createdAt" class="flex flex-col w-1/4">
            <span class="font-bold">Criado em:</span>
            <span>{{ functions.formatDateAndHour(item.createdAt) }}</span>
          </div>
          <div v-if="item.updatedAt" class="flex flex-col w-1/4">
            <span class="font-bold">Atualizado em:</span>
            <span>{{ functions.formatDateAndHour(item.updatedAt) }}</span>
          </div>
        </div>
        <div v-if="item.errorMessage" class="mb-4">
          <span class="font-bold">Erro:</span>
          <span>{{ item.errorMessage }}</span>
        </div>
        <vs-tabs class="mt-3" alignment="fixed">
          <vs-tab label="Logs">
            <div v-if="!item.logs || item.logs.length === 0" class="tab-text">
              Nenhum log encontrado.
            </div>
            <div v-else class="i4pro-logs-detail">
              <div
                v-for="(log, logIndex) in item.logs"
                :key="log.id || logIndex"
                class="i4pro-log-entry"
              >
                <div
                  class="i4pro-log-entry__header flex flex-row items-center mb-3"
                >
                  <span class="font-bold mr-4">{{
                    getActionLabel(log.action)
                  }}</span>
                  <span class="mr-3">{{ log.method }}</span>
                  <vs-chip
                    class="mr-3"
                    :color="getResponseColor(log.responseCode)"
                  >
                    {{ log.responseCode || '---' }}
                  </vs-chip>
                </div>
                <div class="mb-3">
                  <div v-if="log.url" class="mb-2">
                    <span class="font-bold">URL:</span>
                    <span class="break-all">{{ log.url }}</span>
                  </div>
                  <div class="flex flex-row flex-wrap gap-4">
                    <div v-if="log.durationMs" class="flex flex-col w-1/6">
                      <span class="font-bold">Duracao:</span>
                      <span>{{ log.durationMs }} ms</span>
                    </div>
                    <div v-if="log.createdAt" class="flex flex-col w-1/4">
                      <span class="font-bold">Criado em:</span>
                      <span>{{
                        functions.formatDateAndHour(log.createdAt)
                      }}</span>
                    </div>
                  </div>
                </div>
                <vs-tabs class="mt-2" alignment="fixed">
                  <vs-tab label="Request">
                    <div class="tab-text">
                      <div class="flex items-center mb-2">
                        <span class="font-bold">Headers</span>
                        <CopyButton
                          class="ml-2"
                          :value="minifyJson(log.requestHeaders)"
                        />
                      </div>
                      <pre class="json-minified">{{
                        minifyJson(log.requestHeaders)
                      }}</pre>
                      <div class="flex items-center mt-3 mb-2">
                        <span class="font-bold">Body</span>
                        <CopyButton
                          class="ml-2"
                          :value="minifyJson(log.requestBody)"
                        />
                      </div>
                      <pre class="json-minified">{{
                        minifyJson(log.requestBody)
                      }}</pre>
                    </div>
                  </vs-tab>
                  <vs-tab label="Response">
                    <div class="tab-text">
                      <div class="flex items-center mb-2">
                        <span class="font-bold">Headers</span>
                        <CopyButton
                          class="ml-2"
                          :value="minifyJson(log.responseHeaders)"
                        />
                      </div>
                      <pre class="json-minified">{{
                        minifyJson(log.responseHeaders)
                      }}</pre>
                      <div class="flex items-center mt-3 mb-2">
                        <span class="font-bold">Body</span>
                        <CopyButton
                          class="ml-2"
                          :value="minifyJson(log.responseBody)"
                        />
                      </div>
                      <pre class="json-minified">{{
                        minifyJson(log.responseBody)
                      }}</pre>
                    </div>
                  </vs-tab>
                </vs-tabs>
              </div>
            </div>
          </vs-tab>
          <vs-tab label="Payload">
            <div class="tab-text">
              <pre class="json-minified">{{ minifyJson(item.payload) }}</pre>
            </div>
          </vs-tab>
        </vs-tabs>
      </vs-collapse-item>
    </vs-collapse>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import functions from '../../../../utils/functions';
import notify from '../../../../utils/notify';
import i4proApiService from '../../../../services/i4pro.api';
import CopyButton from '../../../../components/CopyButton.vue';

export default {
  name: 'I4ProIntegrationContent',
  components: {
    vSelect,
    CopyButton,
  },
  props: {
    logs: {
      type: Array,
      default: () => [],
    },
    policyId: {
      type: [Number, String],
      required: true,
    },
  },
  computed: {
    computedClass() {
      const hasLogs = Array.isArray(this.logs) && this.logs.length === 0;
      return hasLogs ? 'pb-24' : 'pb-4';
    },
    shouldShowPaidInstallments() {
      return this.selectedAction === 'installment-payment';
    },
    paidInstallmentOptions() {
      if (!Array.isArray(this.paidInstallments)) {
        return [];
      }

      return this.paidInstallments
        .slice()
        .sort((a, b) => {
          const aNumber =
            a &&
            a.installmentNumber !== undefined &&
            a.installmentNumber !== null
              ? Number(a.installmentNumber)
              : 0;
          const bNumber =
            b &&
            b.installmentNumber !== undefined &&
            b.installmentNumber !== null
              ? Number(b.installmentNumber)
              : 0;
          return aNumber - bNumber;
        })
        .map((installment) => {
          const hasPaymentValue =
            installment &&
            installment.paymentValue !== undefined &&
            installment.paymentValue !== null;
          const hasInstallmentValue =
            installment &&
            installment.installmentValue !== undefined &&
            installment.installmentValue !== null;
          const installmentValue = Number(
            hasPaymentValue
              ? installment.paymentValue
              : hasInstallmentValue
              ? installment.installmentValue
              : 0,
          );

          const formattedValue =
            this.functions.formataRealTruncated(installmentValue) || 'R$ 0,00';

          const installmentNumber =
            installment &&
            installment.installmentNumber !== undefined &&
            installment.installmentNumber !== null
              ? installment.installmentNumber
              : '-';

          return {
            label: `Parcela ${installmentNumber} - ${formattedValue}`,
            value:
              installment &&
              installment.installmentNumber !== undefined &&
              installment.installmentNumber !== null
                ? {
                    installment: Number(installment.installmentNumber),
                    installmentId:
                      installment.id !== undefined && installment.id !== null
                        ? Number(installment.id)
                        : null,
                  }
                : null,
          };
        });
    },
    isProcessDisabled() {
      if (!this.selectedAction) {
        return true;
      }
      if (
        this.shouldShowPaidInstallments &&
        (!this.selectedPaidInstallment ||
          this.selectedPaidInstallment.installment === null ||
          this.selectedPaidInstallment.installment === undefined)
      ) {
        return true;
      }
      return false;
    },
  },
  data() {
    return {
      functions,
      selectedAction: null,
      selectedPaidInstallment: null,
      paidInstallments: [],
      isLoadingPaidInstallments: false,
      reprocessingSyncId: null,
      actionOptions: [
        {
          label: 'Emissao de apolice',
          value: 'policy-emission-step1-broker',
        },
        {
          label: 'Emissão de Endosso',
          value: 'endorsement-emission',
        },
        {
          label: 'Baixa',
          value: 'installment-payment',
        },
      ],
    };
  },
  methods: {
    getStatusLabel(status) {
      const labelsByStatus = {
        failed: 'Falha',
        done: 'Sucesso',
        success: 'Sucesso',
        pending: 'Pendente',
      };
      return labelsByStatus[status] || status || '---';
    },
    getStatusColor(status) {
      const colorsByStatus = {
        failed: 'danger',
        done: 'success',
        success: 'success',
        pending: 'warning',
      };
      return colorsByStatus[status] || 'primary';
    },
    canReprocess(item) {
      if (!item || !item.id) {
        return false;
      }

      const normalizedStatus = String(item.status || '').toLowerCase();
      return normalizedStatus === 'failed' || normalizedStatus === 'falha';
    },
    getActionLabel(action) {
      const labelsByAction = {
        'policy-emission-step1-broker': '1. Emissão da apólice - Corretora',
        'policy-emission-step2-vehicle': '2. Emissão da apólice - Veículo',
        'policy-emission-step3-insured': '3. Emissão da apólice - Segurado',
        'policy-emission-step4-proposal': '4. Emissão da apólice - Proposta',
        'policy-emission-step5-policy': '5. Emissão da apólice - Apólice',
        'endorsement-step1-proposal': '1. Endosso - Proposta',
        'endorsement-step2-emission': '2. Endosso - Emissão',
        'installment-payment': 'Pagamento de parcela',
      };
      return labelsByAction[action] || action || '---';
    },
    getResponseColor(code) {
      if (!code) {
        return 'warning';
      }
      if (code >= 200 && code < 300) {
        return 'success';
      }
      if (code >= 400) {
        return 'danger';
      }
      return 'warning';
    },
    minifyJson(value) {
      if (value === null || value === undefined || value === '') {
        return '';
      }
      if (typeof value === 'string') {
        try {
          return JSON.stringify(JSON.parse(value));
        } catch (error) {
          return value;
        }
      }
      try {
        return JSON.stringify(value);
      } catch (error) {
        return String(value);
      }
    },
    reloadData() {
      this.$emit('reload');
    },
    async loadPaidInstallments() {
      this.isLoadingPaidInstallments = true;
      try {
        this.paidInstallments = await i4proApiService.getPaidInstallments(
          this.policyId,
        );
      } catch (error) {
        notify.notifyFail(this.$vs, 'Erro ao buscar parcelas pagas.');
        this.paidInstallments = [];
      } finally {
        this.isLoadingPaidInstallments = false;
      }
    },
    async reprocessSync(item) {
      if (!this.canReprocess(item) || this.reprocessingSyncId === item.id) {
        return;
      }

      this.reprocessingSyncId = item.id;
      this.$vs.loading();
      try {
        await i4proApiService.reprocessSync(item.id);
        notify.notifySuccess(this.$vs, 'Reprocessamento enviado com sucesso.');
        this.reloadData();
      } catch (error) {
        notify.notifyFail(this.$vs, 'Erro ao reprocessar integração.');
      } finally {
        this.reprocessingSyncId = null;
        this.$vs.loading.close();
      }
    },
    async executeManualAction() {
      if (!this.selectedAction) {
        return;
      }
      if (
        this.shouldShowPaidInstallments &&
        (!this.selectedPaidInstallment ||
          this.selectedPaidInstallment.installment === null ||
          this.selectedPaidInstallment.installment === undefined)
      ) {
        notify.notifyFail(this.$vs, 'Selecione uma parcela para processar.');
        return;
      }
      this.$vs.loading();
      try {
        const payload = this.shouldShowPaidInstallments
          ? {
              installment: Number(this.selectedPaidInstallment.installment),
              installmentId:
                this.selectedPaidInstallment.installmentId !== undefined &&
                this.selectedPaidInstallment.installmentId !== null
                  ? Number(this.selectedPaidInstallment.installmentId)
                  : null,
            }
          : undefined;
        await i4proApiService.executeSync(
          this.policyId,
          this.selectedAction,
          payload,
        );
        notify.notifySuccess(this.$vs, 'Processamento enviado com sucesso.');
        this.selectedAction = null;
        this.selectedPaidInstallment = null;
        this.paidInstallments = [];
        this.reloadData();
      } catch (error) {
        notify.notifyFail(this.$vs, 'Erro ao processar acao.');
      } finally {
        this.$vs.loading.close();
      }
    },
  },
  watch: {
    async selectedAction(newValue) {
      if (newValue === 'installment-payment') {
        this.selectedPaidInstallment = null;
        await this.loadPaidInstallments();
        return;
      }
      this.selectedPaidInstallment = null;

      this.paidInstallments = [];
    },
  },
};
</script>

<style scoped>
.break-all {
  word-break: break-all;
}

.i4pro-integration-logs /deep/ .vs-tabs--content {
  overflow: visible;
  height: auto;
}

.i4pro-logs-collapse
  /deep/
  > .vs-collapse-item
  > .vs-collapse-item--content
  > .con-content--item {
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 1rem;
  box-sizing: border-box;
  scrollbar-gutter: stable;
}

.i4pro-logs-detail {
  max-height: 45vh;
  overflow-y: auto;
  padding-right: 1rem;
  box-sizing: border-box;
  scrollbar-gutter: stable;
}

.i4pro-log-entry {
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.i4pro-log-entry__header {
  background: #f3f4f6;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
}

.json-minified {
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 0.85rem;
}

.i4pro-log-entry:last-child {
  border-bottom: 0;
  margin-bottom: 0;
  padding-bottom: 0;
}
</style>
